/**
 * Entidade User (Usuário).
 * Entidade central; company_name é opcional.
 *
 * O `id` é sempre o UID do Firebase Auth (criado automaticamente no login).
 * Esse mesmo UID é a referência em todas as outras tabelas (clientes, serviços, etc.).
 */

import { z } from 'zod';
import { LEGACY_SUBSCRIPTION_FIELDS_ON_USER } from '../subscription/entity';

/** Passos do fluxo de demo guiada (gravado em `users/{uid}.product_demo_last_step`). */
export const PRODUCT_DEMO_STEPS = [
  'whatsapp_test',
  'reminder_settings',
  'service_register',
  'reminders',
  'dashboard',
  'wrap_up',
] as const;

export const productDemoStepSchema = z.enum(PRODUCT_DEMO_STEPS);

export type ProductDemoStep = z.infer<typeof productDemoStepSchema>;

export const userSchema = z.object({
  id: z.string().min(1), // UID do Firebase Auth (atribuído automaticamente no login)
  phone: z.string().min(1),
  /** true quando o login foi por celular (SMS); false quando Google/Meta e o número foi só informado no onboarding. Default false para documentos antigos. */
  phone_verified: z.boolean().default(false),
  email: z.string().nullable(),
  name: z.string().nullable(),
  company_name: z.string().nullable(),
  /** ISO 8601: aceite dos Termos de Uso no onboarding (auditoria LGPD). */
  accepted_terms_at: z.string().min(1).nullable().optional(),
  /** Número da versão dos Termos aceita (mesmo `versionNumber` do admin). */
  accepted_terms_version: z.number().int().positive().nullable().optional(),
  /** ISO 8601: aceite da Política de Privacidade no onboarding. */
  accepted_privacy_at: z.string().min(1).nullable().optional(),
  /** Número da versão da Política aceita (mesmo `versionNumber` do admin). */
  accepted_privacy_version: z.number().int().positive().nullable().optional(),
  /**
   * Quando o usuário dispensou o tutorial de instalação do PWA (pular ou concluir).
   * Ausente = ainda pode exibir o tutorial (com plano ativo).
   */
  pwa_install_tutorial_dismissed_at: z.string().min(1).nullable().optional(),
  /** Como o tutorial foi fechado (auditoria / produto). */
  pwa_install_tutorial_dismiss_kind: z.enum(['skip', 'completed']).nullable().optional(),
  /**
   * Demo guiada do produto (fluxo “sentir o valor” após login).
   * Fica no doc `users/{uid}` junto do perfil: um `getUserById` já traz o estado, sem subcoleção.
   * Subcoleção só faria sentido se o histórico da demo crescesse muito (eventos, versões).
   */
  /** ISO 8601: primeira vez que o utilizador entrou no fluxo de demo. */
  product_demo_started_at: z.string().min(1).nullable().optional(),
  /** ISO 8601: demo concluída até o fim. */
  product_demo_completed_at: z.string().min(1).nullable().optional(),
  /** ISO 8601: utilizador optou por pular a demo (encerra obrigatoriedade do fluxo guiado). */
  product_demo_skipped_at: z.string().min(1).nullable().optional(),
  /**
   * Último passo da demo visto ou pendente (retomada após sair).
   * `null` = ainda não avançou no fluxo ou valor inválido no Firestore.
   */
  product_demo_last_step: productDemoStepSchema.nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type User = z.infer<typeof userSchema>;

function coalesceNullableString(v: unknown): string | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t.length === 0 ? null : t;
}

/** Inteiro positivo opcional (aceites versionados no Firestore). */
function coalesceOptionalPositiveInt(v: unknown): number | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v === 'number' && Number.isInteger(v) && v > 0) return v;
  if (typeof v === 'string') {
    const n = parseInt(v, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

function coalescePwaTutorialDismissKind(v: unknown): 'skip' | 'completed' | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (v === 'skip' || v === 'completed') return v;
  return undefined;
}

function coalesceProductDemoLastStep(v: unknown): ProductDemoStep | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  if (t.length === 0) return null;
  const parsed = productDemoStepSchema.safeParse(t);
  return parsed.success ? parsed.data : null;
}

/**
 * Preenche defaults ao ler Firestore (docs legados / strings vazias).
 */
export function normalizeUserFields(id: string, raw: Record<string, unknown>): Record<string, unknown> {
  const stripped: Record<string, unknown> = {...raw};
  for (const k of LEGACY_SUBSCRIPTION_FIELDS_ON_USER) {
    delete stripped[k];
  }

  const phoneRaw = stripped.phone;
  const phone =
    typeof phoneRaw === 'string' && phoneRaw.trim().length > 0 ? phoneRaw.trim() : '-';

  const nowIso = new Date().toISOString();
  const created_at =
    typeof stripped.created_at === 'string' && stripped.created_at.trim().length > 0
      ? stripped.created_at.trim()
      : nowIso;
  const updated_at =
    typeof stripped.updated_at === 'string' && stripped.updated_at.trim().length > 0
      ? stripped.updated_at.trim()
      : nowIso;

  const email = typeof stripped.email === 'string' ? stripped.email : null;
  const name = typeof stripped.name === 'string' ? stripped.name : null;
  const company_name = typeof stripped.company_name === 'string' ? stripped.company_name : null;

  return {
    ...stripped,
    id,
    phone,
    email,
    name,
    company_name,
    created_at,
    updated_at,
    accepted_terms_at: coalesceNullableString(stripped.accepted_terms_at),
    accepted_terms_version: coalesceOptionalPositiveInt(stripped.accepted_terms_version),
    accepted_privacy_at: coalesceNullableString(stripped.accepted_privacy_at),
    accepted_privacy_version: coalesceOptionalPositiveInt(stripped.accepted_privacy_version),
    pwa_install_tutorial_dismissed_at: coalesceNullableString(stripped.pwa_install_tutorial_dismissed_at),
    pwa_install_tutorial_dismiss_kind: coalescePwaTutorialDismissKind(
      stripped.pwa_install_tutorial_dismiss_kind
    ),
    product_demo_started_at: coalesceNullableString(stripped.product_demo_started_at),
    product_demo_completed_at: coalesceNullableString(stripped.product_demo_completed_at),
    product_demo_skipped_at: coalesceNullableString(stripped.product_demo_skipped_at),
    product_demo_last_step: coalesceProductDemoLastStep(
      stripped.product_demo_last_step ?? stripped.product_demo_last_step_id
    ),
  };
}

/** Referências Firestore da entidade User (paths e strings centralizadas). */
export class UserRef {
  static readonly collection = 'users';
  static docPath(id: string): string {
    return `${this.collection}/${id}`;
  }
}
